import { FC, ReactNode } from 'react';
import { Card, Icon, InteracWordmark, withStyles, formattedCurrency } from 'vopay';
import { Button } from 'UI';
import { Dialog } from '@/providers/Dialog.provider';
import styles from './ProcessFileDialog.module.scss';
import { useTranslation } from 'react-i18next';
import { DetailsRows } from 'UI';

export type ProcessFileSuccessData = {
  transferType: string;
  paymentRail: string;
  alternatePaymentRail?: string;
  processor: string;
  numberOfTransactions: number;
  currency: string;
  totalAmount: string;
  totalFailed: number;
};

type ProcessFileSuccessDialogProps = {
  onClose: () => void;
  data: ProcessFileSuccessData;
};

const cn = withStyles(styles);

const ProcessFileSuccessDialog: FC<ProcessFileSuccessDialogProps> = ({ onClose, data }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'FileUpload.History.FileConversionTable.ProcessFileDialog',
  });

  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  // Map payment rail codes to readable names using tGlobal
  const getPaymentRailLabel = (rail: string) => {
    if (!rail) return '';

    const railMap: Record<string, ReactNode> = {
      eft: tGlobal('PaymentRail.eft'),
      vopayinstant: tGlobal('PaymentRail.vopayinstant'),
      ach: tGlobal('PaymentRail.ach'),
      rtp: tGlobal('PaymentRail.rtp'),
      fednow: tGlobal('PaymentRail.fednow'),
      fedwire: tGlobal('PaymentRail.fedwire'),
      interac: <InteracWordmark />,
      aft: tGlobal('PaymentRail.aft'),
      creditcard: tGlobal('PaymentRail.creditcard'),
      debitcard: tGlobal('PaymentRail.debitcard'),
      oct: tGlobal('PaymentRail.oct'),
    };

    return railMap[rail] || rail;
  };

  const getServiceLabel = (service: string) => {
    if (!service) return '';

    return service === 'credit' ? tGlobal('ServiceOptions.credit') : tGlobal('ServiceOptions.debit');
  };

  const details = [
    { label: t('transferType', { defaultValue: 'Transfer Type' }), value: getServiceLabel(data.transferType) },
    { label: t('paymentRail', { defaultValue: 'Payment Rail' }), value: getPaymentRailLabel(data.paymentRail) },
    ...(data.alternatePaymentRail
      ? [
          {
            label: t('alternativePaymentRail', { defaultValue: 'Fallback Payment Rail' }),
            value: getPaymentRailLabel(data.alternatePaymentRail),
          },
        ]
      : []),
    { label: t('processor', { defaultValue: 'Processor' }), value: data.processor },
    {
      label: t('numberOfTransactions', { defaultValue: 'Number Of Transactions' }),
      value: String(data.numberOfTransactions),
    },
    { label: t('currency', { defaultValue: 'Currency' }), value: data.currency },
    {
      label: t('totalAmount', { defaultValue: 'Total Amount' }),
      value: `$${formattedCurrency(data.totalAmount) || 0}`,
    },
    { label: t('totalFailed', { defaultValue: 'Total Failed' }), value: formattedCurrency(String(data.totalFailed)) },
  ];

  return (
    <Card className={cn('modalContainer')}>
      <div className={cn('content')}>
        <div className={cn('iconContainer')}>
          <div className={cn('successIcon')}>
            <Icon icon="check" height="34px" width="34px" color="primary-light" />
          </div>

          <Dialog.Title level="3">{t('titleSuccess', { defaultValue: 'Submission Successful' })}</Dialog.Title>
        </div>

        <DetailsRows details={details} />

        <Button
          colorScheme="primary"
          onClick={() => {
            onClose();
          }}
        >
          {t('close', { defaultValue: 'Close' })}
        </Button>
      </div>
    </Card>
  );
};

export default ProcessFileSuccessDialog;
